import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Dropdown from "../../../../parts/dropdown";
import SvgIcon from "../../../../parts/SvgIcon";
import SettingsNav from "../../../../parts/settings-nav";
import { isPerformActiveCoach } from "../../../../utils/user-helpers";
import { SettingsBackHandler } from "../../../coaching-portal/settings/components/SettingsBackHandler";
import {
  getRoleNameById,
  getRolesToShow,
  translateOrganizationPermission,
  translateOrganizationRole,
} from "./utils/manage-coaches-utils";

export const InviteNewCoachComponent = ({ locals, roleOrganizationName }) => {
  const { t } = useTranslation("dashboard");
  const {
    organizationPermissions = [],
    organizationRoles = [],
    activeUser,
    organizationOwner,
  } = locals;
  //roles to show in the dropdown
  const rolesToShow = getRolesToShow(organizationRoles, roleOrganizationName);
  //capture the coach role (initial state)
  const coachRoleId = organizationRoles?.find(
    ({ role }) => role === "Coach"
  )._id;
  //handle the selected role
  const [selectedRoleState, setSelectedRoleState] = useState(coachRoleId);
  //this state is because if the role is admin or owner, all the permissions should be enable
  const [selectedRoleNameState, setSelectedRoleNameState] = useState(
    getRoleNameById(organizationRoles, coachRoleId)
  );

  //state for the checkbox
  const [selectedPermissionsState, setSelectedPermissionsState] = useState([
    "",
  ]);
  const [emailValue, setEmailValue] = useState("");

  const userIsPush = Boolean(organizationOwner.pushAccountInfo);
  const userIsPress = Boolean(organizationOwner.pressAccountInfo);
  const userIsPerform = isPerformActiveCoach(organizationOwner);

  //filtering the permissions to show if the user is push, press, or perform
  const filteredPermissions = organizationPermissions.filter(
    ({ permission }) => {
      if (
        (permission !== "PUSH" &&
          permission !== "PRESS" &&
          permission !== "PERFORM") ||
        (permission === "PUSH" && userIsPush) ||
        (permission === "PRESS" && userIsPress) ||
        (permission === "PERFORM" && userIsPerform)
      ) {
        return true;
      }
      return false;
    }
  );

  //to the select all permissions function
  const permissionIds = filteredPermissions.map((permission) => permission._id);

  /**
   * Go back
   */
  const handleGoBack = () => {
    submit(`/actions/refresh/`, {
      method: "GET",
      body: { organizationInviteCoach: "", roleOrganizationName: "" },
    });
  };

  /**
   * Capture the role selected
   * @param {*} roleId
   */
  const handleSetSelectedRole = (roleId) => {
    setSelectedRoleState(roleId);
    const roleName = getRoleNameById(organizationRoles, roleId);
    setSelectedRoleNameState(roleName);
    // Admin/Owner get every permission checked AND locked (see
    // handleSelectionChange/the cursor style below, both still keyed on the
    // literal "Admin"/"Owner" check). Team Coach starts checked too, but
    // stays editable — it isn't part of those other checks.
    if (
      roleName === "Admin" ||
      roleName === "Owner" ||
      roleName === "Team Coach"
    ) {
      selectAll(true);
    }
  };

  /**
   * Handle the selected permissions
   * @param {*} id
   */
  const handleSelectionChange = (id) => {
    if (
      selectedRoleNameState !== "Admin" &&
      selectedRoleNameState !== "Owner"
    ) {
      //code for the check color
      let data = JSON.parse(JSON.stringify(selectedPermissionsState));
      if (!selectedPermissionsState.includes(id)) {
        data.length === 1 && data[0].length === 0
          ? (data = [id])
          : data.push(id);
      } else {
        if (selectedPermissionsState.length > 0) {
          const filetered = data.filter((val) => val !== id);
          data = filetered.length > 0 ? filetered : [""];
        }
      }
      setSelectedPermissionsState(data);
    }
  };

  /**
   * Select all the permissions
   */
  const selectAll = (adminSelected = false) => {
    const reset =
      !adminSelected &&
      selectedRoleNameState !== "Admin" &&
      selectedRoleNameState !== "Owner";
    if (
      JSON.stringify(permissionIds) === JSON.stringify(selectedPermissionsState)
    ) {
      if (reset) {
        //unselect all
        setSelectedPermissionsState([""]);
      }
    } else {
      //select all
      setSelectedPermissionsState(permissionIds);
    }
  };

  /**
   * change email's value
   * @param {*} target
   */
  const handleInputChange = ({ target }) => {
    setEmailValue(target.value);
  };

  /**
   * Send an invitation to the coach
   * @param {*} e
   */
  const handleInvite = (e) => {
    if (validateForm()) {
      let newBody = null;
      const commonProperties = {
        organizationRoleName: selectedRoleNameState,
        organizationPermissions: selectedPermissionsState,
        organizationId: activeUser.organizationId,
        organizationRoleId: selectedRoleState,
        email: emailValue,
        organizationName:
          organizationOwner.businessName || organizationOwner.name,
      };

      newBody = {
        ...commonProperties,
        ...(organizationOwner.firebaseProjectId
          ? { firebaseProjectId: organizationOwner.firebaseProjectId }
          : {}),
        ...(organizationOwner.chatEnabled
          ? { chatEnabled: organizationOwner.chatEnabled }
          : {}),
        ...(organizationOwner.round != null
          ? { round: organizationOwner.round }
          : {}),
      };

      e.stopPropagation();
      e.preventDefault();
      submit(`/actions/organizations/invite-coach`, {
        method: "POST",
        body: newBody,
      });
    }
  };

  /**
   * Check if the email and permissions are not empty
   * @returns Boolean
   */
  const validateForm = useCallback(() => {
    const isValidEmail = () => {
      var re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(emailValue).toLowerCase());
    };
    return selectedPermissionsState[0].length > 0 && isValidEmail();
  }, [emailValue, selectedPermissionsState[0]]);
  const isValidForm = validateForm();

  return (
    <main className="settings">
      <SettingsNav classNames="mobile-hidden" />
      <section className="manage-coaches invite">
        <header>
          <h3 className="force-mobile-hidden">{t("manageCoaches.title")}</h3>
          <SettingsBackHandler
            customBackUrl={"/coaching-portal/settings/manage-coaches"}
            title={t("manageCoaches.inviteNewCoach")}
          />

          <hgroup className="mobile-hidden">
            <button
              data-button-type="secondary"
              onClick={handleGoBack}
              type="button"
            >
              <SvgIcon src={"/img/new-icons/back-button.svg"} />
            </button>
            <p className="s1">{t("manageCoaches.inviteNewCoach")}</p>
          </hgroup>
        </header>
        <div className="content">
          <div className="inputs-right">
            <div>
              <label htmlFor="emailValue">{t("manageCoaches.email")}</label>
              <input
                value={emailValue}
                className="text-input"
                placeholder={t("manageCoaches.enterEmail")}
                name="emailValue"
                id="emailValue"
                onChange={handleInputChange}
                sz="large"
              />
            </div>
            <div>
              <label>{t("manageCoaches.role")}</label>
              <Dropdown
                summaryText={translateOrganizationRole(
                  t,
                  selectedRoleNameState
                )}
                options={rolesToShow.map((role) => {
                  return {
                    text: translateOrganizationRole(t, role.role),
                    value: role._id,
                  };
                })}
                onSelectFn={handleSetSelectedRole}
              />
            </div>
          </div>
          <label>{t("manageCoaches.permissions")}</label>
          <section role="table-wrapper" data-borders-inside>
            <table role="report-table">
              <thead>
                <th>
                  <SvgIcon
                    onClick={() => {
                      selectAll();
                    }}
                    src={`/img/new-icons/${
                      JSON.stringify(permissionIds) ===
                      JSON.stringify(selectedPermissionsState)
                        ? "checked-square.svg"
                        : "unchecked-square.svg"
                    }`}
                    noFilterOnLight={
                      JSON.stringify(permissionIds) ===
                      JSON.stringify(selectedPermissionsState)
                    }
                  />
                </th>
                <th>
                  <p className="s1">{t("manageCoaches.permissions")}</p>
                </th>
              </thead>
              <tbody>
                {filteredPermissions.map((item, idx) => {
                  const { _id, permission } = item;
                  const permissionFormat = translateOrganizationPermission(
                    t,
                    permission
                  );
                  return (
                    <tr key={_id}>
                      <td>
                        {selectedPermissionsState.includes(_id) ? (
                          <SvgIcon
                            src="/img/new-icons/checked-square.svg"
                            alt="checked"
                            style={{
                              cursor:
                                selectedRoleNameState === "Admin" ||
                                selectedRoleNameState === "Owner"
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            onClick={() => handleSelectionChange(_id)}
                            noFilterOnLight
                          />
                        ) : (
                          <SvgIcon
                            src="/img/new-icons/unchecked-square.svg"
                            alt="unchecked"
                            onClick={() => handleSelectionChange(_id)}
                          />
                        )}
                      </td>
                      <td>
                        <span className="permission-name-manage">
                          {permissionFormat}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
          <button
            data-button-type="primary"
            disabled={!isValidForm}
            onClick={handleInvite}
            type="button"
          >
            {t("manageCoaches.invite")}
          </button>
        </div>
      </section>
    </main>
  );
};
